import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  /** Red by default, since most of these take something away. `default` for one that only you undo. */
  confirmVariant?: 'destructive' | 'default';
  /**
   * Anything the question needs answering with, between the description and the buttons. Optional, and
   * empty for every removal: those ask a yes-or-no. Dismissing a remark is the one that offers a reason,
   * and it is still this question with this pair of buttons rather than a dialog of its own.
   */
  children?: React.ReactNode;
  onConfirm: () => void;
}

// Shared confirm-before-remove dialog, so Products / Members / Clients confirm removal the same way.
const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmVariant = 'destructive',
  children,
  onConfirm,
}: ConfirmDialogProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Narrower and roomier than the default dialog: a question and two buttons want air around them
          more than they want width. The footer's negative margins track the padding, so its band still
          runs edge to edge. */}
      <DialogContent className="gap-6 p-8 sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {/* These dialogs hold a couple of lines and a pair of buttons, so the footer's grey band is a big
            share of a small box. Air above it, and less padding inside it, keep the question in front and
            the furniture behind. */}
        <DialogFooter className="-mx-8 -mb-8 mt-8 p-2.5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmLabel ?? t('actions.remove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
